// ToastProvider.jsx
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';
import { registerToast } from './toastService';
import {
  CheckCircle,
  Info,
  AlertTriangle,
  XCircle
} from 'lucide-react';

const TOAST_META = {
  success: { icon: <CheckCircle size={18} />, bg: 'linear-gradient(135deg, #2ecc71, #27ae60)' },
  info: { icon: <Info size={18} />, bg: 'linear-gradient(135deg, #3498db, #2980b9)' },
  warning: { icon: <AlertTriangle size={18} />, bg: 'linear-gradient(135deg, #f1c40f, #f39c12)' },
  error: { icon: <XCircle size={18} />, bg: 'linear-gradient(135deg, #e74c3c, #c0392b)' },
};

export const ToastProvider = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    registerToast((message, type = 'info', duration = 3000) => {
      const id = uuidv4();
      setToasts(prev => [...prev, { id, message, type }]);

      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    });
  }, []);

  const root = document.getElementById('toast-root');
  if (!root) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 10,
      }}
    >
      <AnimatePresence>
        {toasts.map(({ id, message, type }) => {
          const meta = TOAST_META[type] || TOAST_META.info;

          return (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 16px',
                minWidth: 240,
                borderRadius: 12,
                background: meta.bg,
                color: '#fff',
                boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <span style={{ opacity: 0.9 }}>{meta.icon}</span>
              <span>{message}</span>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>,
    root
  );
};
